export const Permission = {
  // User management
  UsersRead: 'users:read',
  UsersCreate: 'users:create',
  UsersUpdate: 'users:update',
  UsersDelete: 'users:delete',

  // Org membership management
  MemberRead: 'members:read',
  MemberInvite: 'members:invite',
  MemberCreate: 'members:create',
  MemberUpdate: 'members:update',
  MemberDelete: 'members:delete',
  MembersAssignRole: 'members:assign_role',
  MembersRemoveRole: 'members:remove_role',

  // Role management
  RolesRead: 'roles:read',
  RolesCreate: 'roles:create',
  RolesUpdate: 'roles:update',
  RolesDelete: 'roles:delete',
  RolesAssign: 'roles:assign',

  //My Org management
  MyOrgRead: 'my_org:read',
  MyOrgUpdate: 'my_org:update',

  // Organizations management
  OrgsRead: 'orgs:read',
  OrgsCreate: 'orgs:create',
  OrgsUpdate: 'orgs:update',
  OrgsDelete: 'orgs:delete',

  // Property management
  PropertiesRead: 'properties:read',
  PropertiesCreate: 'properties:create',
  PropertiesUpdate: 'properties:update',
  PropertiesDelete: 'properties:delete',
  PropertiesHandover: 'properties:handover',

  // Projects management
  ProjectsRead: 'projects:read',
  ProjectsCreate: 'projects:create',
  ProjectsUpdate: 'projects:update',
  ProjectsDelete: 'projects:delete',

  // Locations management
  LocationsRead: 'locations:read',
  LocationsCreate: 'locations:create',
  LocationsUpdate: 'locations:update',
  LocationsDelete: 'locations:delete',

  // Property systems management
  SystemsRead: 'systems:read',
  SystemsCreate: 'systems:create',
  SystemsUpdate: 'systems:update',
  SystemsDelete: 'systems:delete',

  // Property utilities management
  UtilitiesRead: 'utilities:read',
  UtilitiesCreate: 'utilities:create',
  UtilitiesUpdate: 'utilities:update',
  UtilitiesDelete: 'utilities:delete',

  // Items management
  ItemsRead: 'items:read',
  ItemsCreate: 'items:create',
  ItemsUpdate: 'items:update',
  ItemsDelete: 'items:delete',

  // Settings
  SettingsRead: 'settings:read',
  SettingsUpdate: 'settings:update',

  // Billing (system only)
  BillingRead: 'billing:read',
  BillingManage: 'billing:manage',

  // Templates management
  TemplatesRead: 'templates:read',
  TemplatesCreate: 'templates:create',
  TemplatesUpdate: 'templates:update',
  TemplatesDelete: 'templates:delete',

  // Documents management
  DocumentsRead: 'documents:read',
  DocumentsCreate: 'documents:create',
  DocumentsUpdate: 'documents:update',
  DocumentsDelete: 'documents:delete',
} as const;

// Backward-compatible alias so existing `Permissions.*` imports keep working
export const Permissions = Permission;

export type PermissionKey = keyof typeof Permission;
export type PermissionValue = (typeof Permission)[PermissionKey];

export const PermissionGroups = {
  users: {
    label: 'Users',
    permissions: [Permission.UsersRead, Permission.UsersCreate, Permission.UsersUpdate, Permission.UsersDelete],
  },
  members: {
    label: 'Members',
    permissions: [
      Permission.MemberRead,
      Permission.MemberInvite,
      Permission.MemberCreate,
      Permission.MemberUpdate,
      Permission.MemberDelete,
      Permission.MembersAssignRole,
      Permission.MembersRemoveRole,
    ],
  },
  roles: {
    label: 'Roles',
    permissions: [
      Permission.RolesRead,
      Permission.RolesCreate,
      Permission.RolesUpdate,
      Permission.RolesDelete,
      Permission.RolesAssign,
    ],
  },
  myOrganization: {
    label: 'My Organization',
    permissions: [Permission.MyOrgRead, Permission.MyOrgUpdate],
  },
  organizations: {
    label: 'Organizations',
    permissions: [Permission.OrgsRead, Permission.OrgsCreate, Permission.OrgsUpdate, Permission.OrgsDelete],
  },
  properties: {
    label: 'Properties',
    permissions: [
      Permission.PropertiesRead,
      Permission.PropertiesCreate,
      Permission.PropertiesUpdate,
      Permission.PropertiesDelete,
      Permission.PropertiesHandover,
    ],
  },
  projects: {
    label: 'Projects',
    permissions: [
      Permission.ProjectsRead,
      Permission.ProjectsCreate,
      Permission.ProjectsUpdate,
      Permission.ProjectsDelete,
    ],
  },
  locations: {
    label: 'Locations',
    permissions: [
      Permission.LocationsRead,
      Permission.LocationsCreate,
      Permission.LocationsUpdate,
      Permission.LocationsDelete,
    ],
  },
  systems: {
    label: 'Systems',
    permissions: [Permission.SystemsRead, Permission.SystemsCreate, Permission.SystemsUpdate, Permission.SystemsDelete],
  },
  utilities: {
    label: 'Utilities',
    permissions: [
      Permission.UtilitiesRead,
      Permission.UtilitiesCreate,
      Permission.UtilitiesUpdate,
      Permission.UtilitiesDelete,
    ],
  },
  items: {
    label: 'Items',
    permissions: [Permission.ItemsRead, Permission.ItemsCreate, Permission.ItemsUpdate, Permission.ItemsDelete],
  },
  templates: {
    label: 'Templates',
    permissions: [
      Permission.TemplatesRead,
      Permission.TemplatesCreate,
      Permission.TemplatesUpdate,
      Permission.TemplatesDelete,
    ],
  },
  documents: {
    label: 'Documents',
    permissions: [
      Permission.DocumentsRead,
      Permission.DocumentsCreate,
      Permission.DocumentsUpdate,
      Permission.DocumentsDelete,
    ],
  },
  settings: {
    label: 'Settings',
    permissions: [Permission.SettingsRead, Permission.SettingsUpdate],
  },
  billing: {
    label: 'Billing',
    permissions: [Permission.BillingRead, Permission.BillingManage],
  },
} as const;

export const PermissionLabels: Record<string, string> = {
  [Permission.UsersRead]: 'View Users',
  [Permission.UsersCreate]: 'Create Users',
  [Permission.UsersUpdate]: 'Update Users',
  [Permission.UsersDelete]: 'Delete Users',

  [Permission.MemberRead]: 'View Members',
  [Permission.MemberInvite]: 'Invite Members',
  [Permission.MemberCreate]: 'Create Members',
  [Permission.MemberUpdate]: 'Update Members',
  [Permission.MemberDelete]: 'Delete Members',
  [Permission.MembersAssignRole]: 'Assign Member Role',
  [Permission.MembersRemoveRole]: 'Remove Member Role',

  [Permission.RolesRead]: 'View Roles',
  [Permission.RolesCreate]: 'Create Roles',
  [Permission.RolesUpdate]: 'Update Roles',
  [Permission.RolesDelete]: 'Delete Roles',
  [Permission.RolesAssign]: 'Assign Roles',

  [Permission.MyOrgRead]: 'View My Organization',
  [Permission.MyOrgUpdate]: 'Update My Organization',

  [Permission.OrgsRead]: 'View Organizations',
  [Permission.OrgsCreate]: 'Create Organizations',
  [Permission.OrgsUpdate]: 'Update Organizations',
  [Permission.OrgsDelete]: 'Delete Organizations',

  [Permission.PropertiesRead]: 'View Properties',
  [Permission.PropertiesCreate]: 'Create Properties',
  [Permission.PropertiesUpdate]: 'Update Properties',
  [Permission.PropertiesDelete]: 'Delete Properties',
  [Permission.PropertiesHandover]: 'Manage Property Handovers',

  [Permission.ProjectsRead]: 'View Projects',
  [Permission.ProjectsCreate]: 'Create Projects',
  [Permission.ProjectsUpdate]: 'Update Projects',
  [Permission.ProjectsDelete]: 'Delete Projects',

  [Permission.LocationsRead]: 'View Locations',
  [Permission.LocationsCreate]: 'Create Locations',
  [Permission.LocationsUpdate]: 'Update Locations',
  [Permission.LocationsDelete]: 'Delete Locations',

  [Permission.SystemsRead]: 'View Systems',
  [Permission.SystemsCreate]: 'Create Systems',
  [Permission.SystemsUpdate]: 'Update Systems',
  [Permission.SystemsDelete]: 'Delete Systems',

  [Permission.UtilitiesRead]: 'View Utilities',
  [Permission.UtilitiesCreate]: 'Create Utilities',
  [Permission.UtilitiesUpdate]: 'Update Utilities',
  [Permission.UtilitiesDelete]: 'Delete Utilities',

  [Permission.ItemsRead]: 'View Items',
  [Permission.ItemsCreate]: 'Create Items',
  [Permission.ItemsUpdate]: 'Update Items',
  [Permission.ItemsDelete]: 'Delete Items',

  [Permission.SettingsRead]: 'View Settings',
  [Permission.SettingsUpdate]: 'Update Settings',

  [Permission.BillingRead]: 'View Billing',
  [Permission.BillingManage]: 'Manage Billing',

  [Permission.TemplatesRead]: 'View Templates',
  [Permission.TemplatesCreate]: 'Create Templates',
  [Permission.TemplatesUpdate]: 'Update Templates',
  [Permission.TemplatesDelete]: 'Delete Templates',

  [Permission.DocumentsRead]: 'View Documents',
  [Permission.DocumentsCreate]: 'Create Documents',
  [Permission.DocumentsUpdate]: 'Update Documents',
  [Permission.DocumentsDelete]: 'Delete Documents',
};

export function getPermissionLabel(permission: string): string {
  return PermissionLabels[permission] || permission;
}

export function getAllPermissions(): string[] {
  return Object.values(Permission);
}
